import { format, formatDistanceToNow } from "date-fns";
import type {
  FormsReportPreviewResponse,
  ManagerReportResponse,
} from "@/types/reports";

type BasePdfOptions = {
  siteLabel: string;
  reportTypeLabel: string;
  dateRangeLabel: string;
  generatedAtLabel: string;
};

type AttendancePdfOptions = BasePdfOptions & {
  report: ManagerReportResponse;
  variant: "attendance";
};

type FormsPdfOptions = BasePdfOptions & {
  report: FormsReportPreviewResponse;
  variant: "forms";
};

type PdfOptions = AttendancePdfOptions | FormsPdfOptions;

const MAX_LINE_LENGTH = 94;
const LINES_PER_PAGE = 45;
const MAX_SITES = 10;
const MAX_SIGN_INS = 15;
const encoder = new TextEncoder();

const numberFormatter = new Intl.NumberFormat("en-GB");
const hoursFormatter = new Intl.NumberFormat("en-GB", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const marginLeft = 40;
const startY = 800;
const fontSize = 12;
const lineHeight = 16;
const mediaBox = "[0 0 595 842]"; // A4 portrait in points

const SECTION_SEPARATOR =
  "----------------------------------------------------------------------------";
const SUMMARY_LABEL_WIDTH = 24;
const SITE_NAME_WIDTH = 24;
const SITE_NUMBER_WIDTH = 8;
const SITE_WORKERS_WIDTH = 7;
const SITE_FLAGGED_WIDTH = 7;
const SITE_SHIFT_WIDTH = 9;
const FORM_TEMPLATE_WIDTH = 26;
const FORM_SITE_WIDTH = 20;

function truncate(value: string, maxLength: number) {
  if (value.length <= maxLength) {
    return value;
  }
  if (maxLength <= 1) {
    return value.slice(0, maxLength);
  }
  if (maxLength <= 3) {
    return value.slice(0, maxLength);
  }
  return `${value.slice(0, maxLength - 3)}...`;
}

function escapePdfText(value: string) {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");
}

function wrapLine(text: string) {
  if (text.trim() === "") {
    return [""];
  }

  const words = text.split(" ");
  const lines: string[] = [];
  let current = "";

  words.forEach((word) => {
    if (word.length > MAX_LINE_LENGTH) {
      if (current) {
        lines.push(current);
        current = "";
      }
      for (let i = 0; i < word.length; i += MAX_LINE_LENGTH) {
        lines.push(word.slice(i, i + MAX_LINE_LENGTH));
      }
      return;
    }

    if (!current) {
      current = word;
      return;
    }

    if (current.length + word.length + 1 <= MAX_LINE_LENGTH) {
      current = `${current} ${word}`;
      return;
    }

    lines.push(current);
    current = word;
  });

  if (current) {
    lines.push(current);
  }

  return lines;
}

function splitIntoPages(lines: string[]): string[][] {
  if (lines.length === 0) {
    return [[""]];
  }

  const pages: string[][] = [];
  for (let i = 0; i < lines.length; i += LINES_PER_PAGE) {
    pages.push(lines.slice(i, i + LINES_PER_PAGE));
  }
  return pages;
}

function createContentStream(lines: string[]) {
  const commands = [
    "BT",
    `/F1 ${fontSize} Tf`,
    `${lineHeight} TL`,
    `${marginLeft} ${startY} Td`,
  ];

  lines.forEach((line, index) => {
    const safeLine = escapePdfText(line);
    if (index === 0) {
      commands.push(`(${safeLine}) Tj`);
      return;
    }
    commands.push("T*");
    commands.push(`(${safeLine}) Tj`);
  });

  commands.push("ET");
  const content = commands.join("\n");
  const length = encoder.encode(content).length;
  return { content, length };
}

function buildPdf(pages: string[][]) {
  let nextId = 1;
  const objects: Array<{ id: number; body: string }> = [];

  const catalogId = nextId++;
  const pagesId = nextId++;
  const fontId = nextId++;

  objects.push({
    id: fontId,
    body: "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>",
  });

  const pageEntries = pages.map((pageLines) => {
    const contentId = nextId++;
    const pageId = nextId++;
    const { content, length } = createContentStream(pageLines);
    objects.push({
      id: contentId,
      body: `<< /Length ${length} >>\nstream\n${content}\nendstream`,
    });
    objects.push({
      id: pageId,
      body: `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox ${mediaBox} /Contents ${contentId} 0 R /Resources << /Font << /F1 ${fontId} 0 R >> >> >>`,
    });
    return pageId;
  });

  const kids = pageEntries.map((pageId) => `${pageId} 0 R`).join(" ");

  objects.push({
    id: pagesId,
    body: `<< /Type /Pages /Kids [${kids}] /Count ${pageEntries.length} >>`,
  });

  objects.push({
    id: catalogId,
    body: `<< /Type /Catalog /Pages ${pagesId} 0 R >>`,
  });

  objects.sort((a, b) => a.id - b.id);

  const parts: string[] = ["%PDF-1.4\n"];
  let byteLength = encoder.encode(parts[0]).length;
  const offsets: number[] = [0];

  const append = (chunk: string) => {
    parts.push(chunk);
    byteLength += encoder.encode(chunk).length;
  };

  objects.forEach((object) => {
    const header = `${object.id} 0 obj\n${object.body}\nendobj\n`;
    offsets[object.id] = byteLength;
    append(header);
  });

  const xrefOffset = byteLength;
  append("xref\n");
  append(`0 ${objects.length + 1}\n`);
  append("0000000000 65535 f \n");
  for (let i = 1; i <= objects.length; i += 1) {
    const offset = offsets[i] ?? 0;
    append(`${offset.toString().padStart(10, "0")} 00000 n \n`);
  }
  append(`trailer << /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\n`);
  append(`startxref\n${xrefOffset}\n%%EOF`);

  return encoder.encode(parts.join(""));
}

function buildAttendanceLines({ report }: { report: ManagerReportResponse }) {
  const lines: string[] = [];

  lines.push("SUMMARY");
  lines.push(SECTION_SEPARATOR);
  const summaryRows: Array<[string, string]> = [
    ["Total sign-ins", numberFormatter.format(report.summary.totalSignIns)],
    ["Unique workers", numberFormatter.format(report.summary.uniqueWorkers)],
    ["Flagged sign-ins", numberFormatter.format(report.summary.flaggedSignIns)],
    [
      "Hours worked",
      `${hoursFormatter.format(report.summary.totalHoursWorked)} h`,
    ],
    [
      "Average shift",
      `${hoursFormatter.format(report.summary.averageShiftHours)} h`,
    ],
  ];

  summaryRows.forEach(([label, value]) => {
    lines.push(`${label.padEnd(SUMMARY_LABEL_WIDTH)} ${value}`);
  });
  lines.push(SECTION_SEPARATOR);
  lines.push("");

  const siteCount = report.siteBreakdown.length;
  const sitesToShow = report.siteBreakdown.slice(0, MAX_SITES);
  lines.push("SITE BREAKDOWN");
  lines.push(
    siteCount > MAX_SITES
      ? `Showing top ${sitesToShow.length} of ${siteCount} managed sites`
      : `Total managed sites in range: ${siteCount}`
  );
  lines.push(SECTION_SEPARATOR);
  lines.push(
    `${"Site".padEnd(SITE_NAME_WIDTH)} ` +
      `${"Sign-ins".padStart(SITE_NUMBER_WIDTH)} ` +
      `${"Workers".padStart(SITE_WORKERS_WIDTH)} ` +
      `${"Flagged".padStart(SITE_FLAGGED_WIDTH)} ` +
      `${"Avg shift".padStart(SITE_SHIFT_WIDTH)} ` +
      `Last activity`
  );
  lines.push(SECTION_SEPARATOR);

  if (sitesToShow.length === 0) {
    lines.push("No sign-ins recorded for these filters.");
  } else {
    sitesToShow.forEach((site) => {
      const lastSeen = site.lastSignInTime
        ? formatDistanceToNow(new Date(site.lastSignInTime), {
            addSuffix: true,
          })
        : "No activity recorded";
      const name = truncate(site.siteName, SITE_NAME_WIDTH).padEnd(
        SITE_NAME_WIDTH
      );
      const signIns = numberFormatter
        .format(site.signInCount)
        .padStart(SITE_NUMBER_WIDTH);
      const workers = numberFormatter
        .format(site.uniqueWorkers)
        .padStart(SITE_WORKERS_WIDTH);
      const flagged = numberFormatter
        .format(site.flaggedSignIns)
        .padStart(SITE_FLAGGED_WIDTH);
      const avgShift =
        `${hoursFormatter.format(site.averageShiftHours)} h`.padStart(
          SITE_SHIFT_WIDTH
        );
      lines.push(
        `${name} ${signIns} ${workers} ${flagged} ${avgShift} ${lastSeen}`
      );
    });
  }

  lines.push(SECTION_SEPARATOR);
  lines.push("");

  const signInCount = report.recentSignIns.length;
  const signInsToShow = report.recentSignIns.slice(0, MAX_SIGN_INS);
  lines.push("RECENT SIGN-INS");
  lines.push(
    signInCount > MAX_SIGN_INS
      ? `Latest ${signInsToShow.length} of ${signInCount} sign-ins`
      : `Total sign-ins in range: ${signInCount}`
  );
  lines.push(SECTION_SEPARATOR);

  if (signInsToShow.length === 0) {
    lines.push("No sign-ins captured for these filters.");
  } else {
    signInsToShow.forEach((entry) => {
      const worker = truncate(entry.workerName ?? "Unknown worker", 26).padEnd(
        26
      );
      const site = truncate(entry.siteName, 20).padEnd(20);
      const status = entry.flagged ? "Flagged" : "Clear";
      lines.push(`${worker} ${site} Status: ${status}`);

      const signedIn = format(new Date(entry.signInTime), "MMM d, yyyy HH:mm");
      const signedOut = entry.signOutTime
        ? format(new Date(entry.signOutTime), "MMM d, yyyy HH:mm")
        : "Still signed in";
      const jobTitle = entry.jobTitle ? `Role: ${entry.jobTitle}` : undefined;
      const timingLine = `  In: ${signedIn}  Out: ${signedOut}`;
      lines.push(timingLine);
      if (jobTitle) {
        lines.push(`  ${jobTitle}`);
      }
      lines.push("");
    });
  }

  if (signInCount > MAX_SIGN_INS) {
    lines.push(
      `Showing ${MAX_SIGN_INS} of ${signInCount} sign-ins. Export CSV for the full list.`
    );
  }

  lines.push(SECTION_SEPARATOR);

  return lines;
}

function buildFormsLines({ report }: { report: FormsReportPreviewResponse }) {
  const lines: string[] = [];

  lines.push("SUMMARY");
  lines.push(SECTION_SEPARATOR);
  const summaryRows: Array<[string, string]> = [
    [
      "Total submissions",
      numberFormatter.format(report.summary.totalSubmissions),
    ],
    ["Pending review", numberFormatter.format(report.summary.pendingReview)],
    ["Approved", numberFormatter.format(report.summary.approved)],
    ["Rejected", numberFormatter.format(report.summary.rejected)],
    ["Closed", numberFormatter.format(report.summary.closed)],
    [
      "Unique templates",
      numberFormatter.format(report.summary.uniqueTemplates),
    ],
  ];

  summaryRows.forEach(([label, value]) => {
    lines.push(`${label.padEnd(SUMMARY_LABEL_WIDTH)} ${value}`);
  });
  lines.push(SECTION_SEPARATOR);
  lines.push("");

  const previewCount = report.preview.length;
  lines.push("RECENT SUBMISSIONS");
  lines.push(
    previewCount === 0
      ? "No submitted forms matched these filters."
      : `Latest ${previewCount} submitted forms in range`
  );
  lines.push(SECTION_SEPARATOR);

  if (previewCount === 0) {
    lines.push("No submitted forms captured for these filters.");
  } else {
    report.preview.forEach((entry) => {
      const template = truncate(
        entry.templateName ?? "Untitled template",
        FORM_TEMPLATE_WIDTH
      ).padEnd(FORM_TEMPLATE_WIDTH);
      const site = truncate(entry.siteName, FORM_SITE_WIDTH).padEnd(
        FORM_SITE_WIDTH
      );
      const status = entry.approvalStatus
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
      lines.push(`${template} ${site} Status: ${status}`);

      const submittedBy =
        entry.submittedByName ?? entry.submittedByEmail ?? "Unknown worker";
      const submittedAt = format(
        new Date(entry.submittedAt),
        "MMM d, yyyy HH:mm"
      );
      lines.push(`  Submitted by: ${submittedBy}`);
      lines.push(`  Submitted at: ${submittedAt}`);
      lines.push("");
    });
  }

  lines.push(
    "Export CSV for the full submission dataset, including captured form fields."
  );
  lines.push(SECTION_SEPARATOR);

  return lines;
}

function buildLines({
  siteLabel,
  reportTypeLabel,
  dateRangeLabel,
  generatedAtLabel,
  ...options
}: PdfOptions) {
  const lines: string[] = [];

  const headerLine = reportTypeLabel.toUpperCase();
  const headerUnderline = "=".repeat(Math.min(headerLine.length, 70));
  lines.push(headerLine);
  lines.push(headerUnderline);
  lines.push(`Generated : ${generatedAtLabel}`);
  lines.push(`Site      : ${siteLabel}`);
  lines.push(`Date range: ${dateRangeLabel}`);
  lines.push("");

  if (options.variant === "forms") {
    lines.push(...buildFormsLines({ report: options.report }));
  } else {
    lines.push(...buildAttendanceLines({ report: options.report }));
  }

  return lines.flatMap((line) => wrapLine(line));
}

export function createReportPdf(options: PdfOptions) {
  const wrappedLines = buildLines(options);
  const pages = splitIntoPages(wrappedLines);
  const pdfBytes = buildPdf(pages);
  return pdfBytes;
}
